import apiBase from "@/api/configBasic";
import apiBaseEsupplyvet from "./configBasicEsupplyvet";
import { useRegisterStore } from "@/stores/register";

function getApiInstance() {
  const registerStore = useRegisterStore();
  const userRole = registerStore.user?.role || "";
  return userRole === "client" ? apiBaseEsupplyvet : apiBase;
}

/**
 * Updates the veterinarian's subscription data
 * @param {string|number} id - The veterinarian's customer ID
 * @param {Object} payload - The subscription data to update
 * @param {string} payload.fraisPort - The shipping fee option ("Frais de port offert" or "Frais de port à la charge du client")
 * @param {number} payload.francoAPartirDe - The minimum order amount for free shipping (in €HT)
 * @returns {Promise<Object>} API response containing the updated subscription data
 */
export async function updateVeterinaireSubscription(id, payload) {
  const httpBaseClient = getApiInstance();
  try {
    const response = await httpBaseClient.post(`/api/v2/shop/select-taxon/${id}`, payload);
    return response.data;
  } catch (error) {
    console.error(`Error updating veterinarian subscription for ID ${id}:`, error);
    throw error;
  }
}

export async function updateVeterinaireFrais(id, payload) {
  const httpBaseClient = getApiInstance();
  try {
    const response = await httpBaseClient.patch(`/api/v2/shop/subscription-users/${id}`, payload);
    return response.data;
  } catch (error) {
    console.error(`Error updating veterinarian subscription for ID ${id}:`, error);
    throw error;
  }
}

export const fetchProductVariantPriceCoefs = async (code = null) => {
  const httpBaseClient = getApiInstance();
  try {
    let url = `/api/v2/shop/product-variant-price-coefs`;
    if (code) {
      url += `?productVariant.product.productTaxons.taxon.code=${encodeURIComponent(code)}`;
    }
    const response = await httpBaseClient.get(url);
    const data = Array.isArray(response.data) ? response.data : response.data["hydra:member"] || [];

    return data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error fetching product variant price coefficients");
  }
};

export const updateProductVariantPriceCoef = async (id, payload) => {
  const httpBaseClient = getApiInstance();
  try {
    const response = await httpBaseClient.patch(`/api/v2/shop/product-variant-price-coefs/${id}`, payload);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error updating product variant price coefficient");
  }
};

export const applyRecommendedPrices = async id => {
  const httpBaseClient = getApiInstance();
  try {
    const response = await httpBaseClient.post(
      `/api/v2/shop/appliquer-prix-conseille/${id}`,
      {},
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Erreur lors de l'application des prix conseillés");
  }
};

/**
 * Fetches a product by its code
 * @param {string} code - The product code
 * @returns {Promise<Object>} Product data with { code, id, name }
 * @throws {Error} If the request fails or the product is not found
 */
export const fetchProductByCode = async (code) => {
  const httpBaseClient = getApiInstance();
  try {
    const response = await httpBaseClient.get(`/api/v2/shop/products/${code}`, {
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
    });
    const product = response.data;
    return {
      code: product.code,
      id: product.id,
      name: product.name || `Produit ${product.code}`,
    };
  } catch (error) {
    console.error(`Error fetching product by code ${code}:`, error);
    throw new Error(error.response?.data?.message || `Erreur lors de la récupération du produit par code ${code}`);
  }
};

/**
 * Fetches a product by its ID
 * @param {string|number} id - The product ID
 * @returns {Promise<Object>} Product data with { code, id, name }
 * @throws {Error} If the request fails or the product is not found
 */
export const fetchProductById = async (id) => {
  const httpBaseClient = getApiInstance();
  try {
    const response = await httpBaseClient.get(`/api/v2/shop/products-by-id/${id}`, {
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
    });
    const product = response.data;
    return {
      code: product.code,
      id: product.id,
      name: product.name || `Produit ${product.id}`,
    };
  } catch (error) {
    console.error(`Error fetching product by ID ${id}:`, error);
    throw new Error(error.response?.data?.message || `Erreur lors de la récupération du produit par ID ${id}`);
  }
};


/**
 * Fetches all communication campaigns
 * @returns {Promise<Array>} Array of communication campaigns
 * @throws {Error} If the request fails
 */
export const fetchCommunications = async () => {
  const httpBaseClient = getApiInstance();
  try {
    const response = await httpBaseClient.get('/api/v2/shop/communication-esupplyvets', {
      headers: { Accept: 'application/ld+json' },
    });
    return response.data["hydra:member"] || [];
  } catch (error) {
    console.error('Error fetching communications:', error);
    throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des communications');
  }
};

/**
 * Changes the status of a communication campaign for a veterinarian
 * @param {string|number} idCommunication - The communication campaign ID
 * @returns {Promise<Object>} API response
 * @throws {Error} If the request fails
 */
export const changeCommunicationStatus = async (idCommunication, enabled) => {
  const httpBaseClient = getApiInstance();
  try {
    const response = await httpBaseClient.post(
      `/api/v2/shop/communication-esupplyvet/veterinarian/change-status/${idCommunication}`,
      { enabled_veterinary: enabled }
    );
    return response.data;
  } catch (error) {
    console.error(`Error changing communication status for ID ${idCommunication}:`, error);
    throw new Error(error.response?.data?.message || 'Erreur lors du changement de statut de la communication');
  }
};



/**
 * Fetches all communication categories
 * @returns {Promise<Array>} Array of categories
 * @throws {Error} If the request fails
 */
export const fetchCommunicationCategories = async () => {
  const httpBaseClient = getApiInstance();
  try {
    const response = await httpBaseClient.get('/api/v2/shop/communication-esupplyvet-categories', {
      headers: { Accept: 'application/ld+json' },
    });
    return response.data["hydra:member"] || [];
  } catch (error) {
    console.error('Error fetching communication categories:', error);
    throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des catégories');
  }
};

/**
 * Toggles the status of communications by category
 * @param {string|number} idCategorie - The category ID
 * @param {boolean} enabledVeterinary - The new status (true/false)
 * @returns {Promise<Object>} API response
 * @throws {Error} If the request fails
 */
export const toggleStatusByCategorie = async (idCategorie, enabledVeterinary) => {
  const httpBaseClient = getApiInstance();
  try {
    const response = await httpBaseClient.post(
      '/api/v2/shop/communication-esupplyvet/veterinarian/toggle-status-by-categorie',
      {
        id_categorie: idCategorie,
        enabled_veterinary: enabledVeterinary
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error toggling status by category:', error);
    throw new Error(error.response?.data?.message || 'Erreur lors du changement de statut par catégorie');
  }
};

/**
 * Fetches the list of new product variant notifications
 * @returns {Promise<Array>} Array of notifications
 */
export const fetchNewProductNotifications = async () => {
  const httpBaseClient = getApiInstance();
  try {
    const response = await httpBaseClient.get('/api/v2/shop/new-product-variant-notifications', {
      headers: { Accept: 'application/ld+json' },
    });

    console.log(response.data);
    return Array.isArray(response.data) ? response.data : response.data["hydra:member"] || [];
  } catch (error) {
    console.error('Error fetching new product notifications:', error);
    throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des nouvelles notifications');
  }
};

/**
 * Confirms a new product variant notification
 * @param {string|number} id - The notification ID
 * @param {Object} payload - The confirmation payload
 * @returns {Promise<Object>} API response
 */
export const confirmProductVariantNotification = async (id, payload) => {
  const httpBaseClient = getApiInstance();
  try {
    const response = await httpBaseClient.post(`/api/v2/shop/new-product-variant-notifications/${id}/confirm`, payload);
    return response.data;
  } catch (error) {
    console.error(`Error confirming notification ${id}:`, error);
    throw new Error(error.response?.data?.message || 'Erreur lors de la confirmation de la notification');
  }
};
